import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Building2, ImageUp, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useRemoveCompanyLogo, useUploadCompanyLogo } from '../hooks/useCompanyLogo';
import { logoProblem } from '../lib/companyLogo';

interface Props {
  companyId: string;
  logoUrl: string | null | undefined;
  /** The company's web domain, offered as a hint for finding a logo (never fetched). */
  domain?: string | null;
  className?: string;
}

/** The logo route answers `{ error, message? }`; api.ts replaces 403/5xx text, so prefer the body. */
function serverReason(e: unknown): string {
  if (e instanceof ApiError && e.data && typeof e.data === 'object') {
    const { message, error } = e.data as { message?: unknown; error?: unknown };
    if (typeof message === 'string' && message) return message;
    if (typeof error === 'string' && error) return error;
  }
  return e instanceof Error ? e.message : String(e);
}

export function CompanyLogoField({ companyId, logoUrl, domain, className }: Props) {
  const { t } = useTranslation('crm');
  const input = useRef<HTMLInputElement>(null);
  const upload = useUploadCompanyLogo(companyId);
  const remove = useRemoveCompanyLogo(companyId);
  const busy = upload.isPending || remove.isPending;

  const take = (file: File | undefined) => {
    if (!file) return;
    const problem = logoProblem(file);
    if (problem) {
      toast.error(t(problem === 'type' ? 'companyLogo.wrongType' : 'companyLogo.tooLarge'));
      return;
    }
    upload.mutate(file, {
      onSuccess: () => toast.success(t('companyLogo.uploaded')),
      onError: (e) => toast.error(t('companyLogo.uploadFailed'), { description: serverReason(e) }),
    });
  };

  return (
    <div
      className={cn('flex items-center gap-3', className)}
      onPaste={(e) => {
        const file = e.clipboardData.files[0];
        if (file) { e.preventDefault(); take(file); }
      }}
    >
      <div
        tabIndex={0}
        aria-label={t('companyLogo.pasteTarget')}
        className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-white"
      >
        {logoUrl
          ? <img src={logoUrl} alt={t('companyLogo.alt')} className="max-h-full max-w-full object-contain" />
          : <Building2 className="h-6 w-6 text-muted-foreground" />}
      </div>
      <div className="space-y-1">
        <div className="flex gap-1">
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => input.current?.click()}>
            <ImageUp className="h-4 w-4 mr-1" /> {logoUrl ? t('companyLogo.replace') : t('companyLogo.upload')}
          </Button>
          {logoUrl && (
            <Button
              type="button" size="sm" variant="ghost" disabled={busy} aria-label={t('companyLogo.remove')}
              onClick={() => remove.mutate(undefined, {
                onSuccess: () => toast.success(t('companyLogo.removed')),
                onError: (e) => toast.error(t('companyLogo.removeFailed'), { description: serverReason(e) }),
              })}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {t('companyLogo.hint')}{!logoUrl && domain ? ` ${t('companyLogo.domainHint', { domain })}` : ''}
        </p>
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          aria-label={t('companyLogo.file')}
          onChange={(e) => { take(e.target.files?.[0]); e.target.value = ''; }}
        />
      </div>
    </div>
  );
}
